import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, IconButton, Modal, Paper, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useState } from 'react';
import { AddFolderButton } from '../../atoms/folder-label/AddFolderButton';
import { FolderInputRow } from '../../molecules/folder-label/FolderInputRow';
import { FolderTree } from '../../organisms/folder-label/FolderTree';
import { useFolderLabel } from '../../../hooks/useFolderLabel';

const useStyles = makeStyles(() => ({
    modalPaper: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%',
        maxWidth: '896px',
        maxHeight: '85vh',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        padding: '24px',
        borderBottom: '1px solid #e5e7eb',
    },
    title: {
        fontSize: '24px',
        fontWeight: 700,
        color: '#1f2937',
    },
    content: {
        padding: '24px',
        flex: 1,
        overflowY: 'auto',
    },
    footer: {
        padding: '24px',
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
    },
    doneButton: {
        width: '100%',
        height: 45,
        borderRadius: 16,
        background: 'linear-gradient(90deg, #17b7a5 0%, #0e9486 100%)',
        color: '#fff',
        fontSize: '1rem',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        '&:hover': {
            background: 'linear-gradient(90deg, #0e9486 0%, #17b7a5 100%)',
        },
    },
}));

export const FolderManagementModal: React.FC<{
    open: boolean,
    onClose: () => void,
}> = ({ open, onClose }) => {
    const classes = useStyles();
    const { folders, addFolder, deleteFolder, addSubfolder } = useFolderLabel();
    const [showAddMasterFolder, setShowAddMasterFolder] = useState<boolean>(false);
    const [newFolderName, setNewFolderName] = useState<string>('');

    const handleAddMasterFolder = useCallback(() => {
        if (newFolderName.trim()) {
            addFolder(newFolderName.trim());
            setNewFolderName('');
            setShowAddMasterFolder(false);
        }
    }, [newFolderName, addFolder]);

    const handleCancelAdd = useCallback(() => {
        setShowAddMasterFolder(false);
        setNewFolderName('');
    }, []);

    const handleShowAddFolder = useCallback(() => setShowAddMasterFolder(true), []);

    return (
        <Modal open={open} onClose={onClose}>
            <Paper className={classes.modalPaper}>
                <Box className={classes.header}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography className={classes.title}>Manage Folders</Typography>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Box>

                <Box className={classes.content}>
                    <Box mb={2}>
                        <Typography variant="subtitle1" fontWeight={600} color="textSecondary" mb={1}>
                            Folder Structure
                        </Typography>
                        <Typography variant="body2" color="textSecondary" mb={2}>
                            Create your folder organization. Click "+ Add Subfolder" on any folder to
                            create nested folders.
                        </Typography>
                        <FolderTree
                            folders={folders}
                            onDelete={deleteFolder}
                            onAddSubfolder={addSubfolder}
                        />
                    </Box>

                    {showAddMasterFolder ? (
                        <FolderInputRow
                            value={newFolderName}
                            onChange={setNewFolderName}
                            onSubmit={handleAddMasterFolder}
                            onCancel={handleCancelAdd}
                            placeholder="Enter folder name..."
                        />
                    ) : (
                        <AddFolderButton onClick={handleShowAddFolder} />
                    )}
                </Box>

                <Box className={classes.footer}>
                    <Button onClick={onClose} className={classes.doneButton}>
                        Done
                    </Button>
                </Box>
            </Paper>
        </Modal>
    );
};
